import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import NeuralNetwork from '../components/NeuralNetwork';

function Products() {
  return (
    <div className="page-wrapper">
      <Helmet>
        <title>Bank Pricing Optimisation Software | Clariti AI Products</title>
        <meta name="description" content="Explore Clariti AI pricing optimisation software for mortgage and savings teams. Improve deposit strategy, margin performance, and pricing decisions with data-driven analytics." />
        <link rel="canonical" href="https://www.clariti.ai/products" />
        <meta property="og:title" content="Bank Pricing Optimisation Software | Clariti AI Products" />
        <meta property="og:description" content="Mortgage and savings pricing optimisation software for banks, built to improve margin and growth with data-backed decisions." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.clariti.ai/products" />
        <meta name="twitter:title" content="Bank Pricing Optimisation Software | Clariti AI Products" />
        <meta name="twitter:description" content="Data-driven pricing software for mortgage and savings teams in global banking markets." />
      </Helmet>
      {/* Products Overview */}
      <section className="section section-light section-top-offset">
        <NeuralNetwork className="nn-layer" nodeCount={20} interactive={true} />
        <div className="container">
          <div className="section-header">
            <h1>Bank pricing optimisation products</h1>
            <p>
              Whether you're optimising mortgage rates or deposit pricing, our bank pricing
              optimisation software gives your team the analytics needed to make confident decisions.
            </p>
          </div>
          <div className="products-grid">
            <div className="product-card">
              <div className="product-icon">
                <HomeIcon />
              </div>
              <h3>Mortgage Rate Optimisation</h3>
              <p>
                Set optimal mortgage rates that balance volume, margin, and risk. 
                Built for pricing teams who need clarity in a complex market.
              </p>
              <Link to="/products/mortgage" className="product-link">
                Learn more <ArrowIcon />
              </Link>
            </div>
            <div className="product-card">
              <div className="product-icon">
                <PiggyBankIcon />
              </div>
              <h3>Savings Rate Optimisation</h3>
              <p>
                Price deposits strategically to manage funding costs and customer 
                retention. Data-driven insights for treasury and savings teams.
              </p>
              <Link to="/products/savings" className="product-link">
                Learn more <ArrowIcon />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Both Matter */}
      <section className="section">
        <NeuralNetwork className="nn-layer" nodeCount={18} interactive={false} />
        <div className="container">
          <div className="section-header">
            <h2>Precision pricing. Powered by data.</h2>
            <p>
              Pricing involves hundreds of moving parts. We cut through the complexity, 
              giving you full visibility into every trade-off so you can focus on what 
              actually drives results.
            </p>
          </div>
          <div className="why-grid">
            <div className="why-card">
              <div className="why-icon">
                <AnalyticsIcon />
              </div>
              <div className="why-card-text">
                <h3>Replace manual workflows with scalable analytics</h3>
                <p>
                  Move beyond spreadsheets to a platform that updates in real time, 
                  keeping your pricing aligned with market conditions as they evolve.
                </p>
              </div>
            </div>
            <div className="why-card">
              <div className="why-icon">
                <InsightIcon />
              </div>
              <div className="why-card-text">
                <h3>Turn market data into actionable insight</h3>
                <p>
                  Go beyond observing competitor moves. Understand what they mean for 
                  your portfolio, your margins, and your competitive positioning.
                </p>
              </div>
            </div>
            <div className="why-card">
              <div className="why-icon">
                <StreamlineIcon />
              </div>
              <div className="why-card-text">
                <h3>See the impact before you act</h3>
                <p>
                  Use your portfolio data to model exactly what happens when rates 
                  change — the effect on volumes, margins, and your bottom line — so 
                  every pricing decision is informed, not intuitive.
                </p>
              </div>
            </div>
          </div>
          <p className="why-closing">
            Proactive, data-driven pricing is the foundation of sustained competitive advantage.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <NeuralNetwork className="nn-layer" nodeCount={30} interactive={true} />
        <div className="container">
          <div className="cta-content">
            <h2>Ready to optimise your pricing?</h2>
            <p>
              See how Clariti AI can help your team make smarter, faster pricing decisions.
            </p>
            <Link to="/contact" className="btn btn-white">Book a Demo</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// Icons
function HomeIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
      <polyline points="9 22 9 12 15 12 15 22"></polyline>
    </svg>
  );
}

function PiggyBankIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 5c-1.5 0-2.8 1.4-3 2-3.5-1.5-11-.3-11 5 0 1.8 0 3 2 4.5V20h4v-2h3v2h4v-4c1-.5 1.7-1 2-2h2v-4h-2c0-1-.5-1.5-1-2V5z"></path>
      <path d="M2 9v1c0 1.1.9 2 2 2h1"></path>
      <path d="M16 11h.01"></path>
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12"></line>
      <polyline points="12 5 19 12 12 19"></polyline>
    </svg>
  );
}

function AnalyticsIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"></rect>
      <line x1="8" y1="16" x2="8" y2="12"></line>
      <line x1="12" y1="16" x2="12" y2="8"></line>
      <line x1="16" y1="16" x2="16" y2="10"></line>
      <polyline points="7 10 12 6 16 8" strokeWidth="1.5"></polyline>
    </svg>
  );
}

function InsightIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"></circle>
      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
      <polyline points="8 11 10 13 14 9"></polyline>
    </svg>
  );
}

function StreamlineIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="5" cy="6" r="2"></circle>
      <circle cx="12" cy="12" r="2"></circle>
      <circle cx="19" cy="6" r="2"></circle>
      <path d="M7 6h10"></path>
      <path d="M5 8v2a2 2 0 0 0 2 2h3"></path>
      <path d="M19 8v2a2 2 0 0 1-2 2h-3"></path>
      <line x1="12" y1="14" x2="12" y2="20"></line>
      <polyline points="9 18 12 21 15 18"></polyline>
    </svg>
  );
}

export default Products;

